/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState, useRef, useMemo, useCallback} from 'react'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'
import {Helmet} from 'react-helmet'
// Components
import {Box, Skeleton, Flex, Container, useDisclosure} from '@chakra-ui/react'
import {
    ProductQuickviewModal,
    useProductQuickviewModal
} from '../../components/product-quickview-modal'
import {useUnifiedProduct} from '../../hooks/use-unified-product'
import lazyLoadable from '../../utils/lazy-loadable'
import PdpSubnavigation from './partials/pdp-subnavigation'
import AfterSales from './partials/after-sales'
// Hooks
import {GenericModal, useGenericModal} from '../../hooks/use-generic-modal'
import useEinstein from '../../hooks/use-einstein'
import CMSContent from '../../components/cms-content'
import ImageGallerySkeleton from '../../components/image-gallery/skeleton'
import {useHeader} from '../../hooks/use-header'
import useOcapiSite from '../../hooks/useOcapiSite'
import useProductReviewOverview from '../../bazaarvoice-api/hooks/use-product-review-overview'
import useWriteProductReviewModal from '../../bazaarvoice-api/hooks/use-write-product-review-modal'
// Project Components
import EmotionalGallerySkeleton from '../../components/emotional-gallery/emotional-gallery-skeleton'
import ProductUSPSkeleton from './partials/product-usp-skeleton'
// Others/Utils
import {HTTPError, HTTPNotFound} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import {getBVReviewProductIds, isProductSet} from '../../utils/product-utils'
// constant
import {
    MAX_CACHE_AGE,
    MINIMUN_NUM_OF_RECOMMENDED_PRODUCTS,
    RECOMMENDED_ACCESSORIES_TYPE,
    STALE_WHILE_REVALIDATE
} from '../../constants'
import Cockpit from './partials/cockpit'
import ProductSetCockpit from './partials/product-set-cockpit'
import CockpitSkeleton from './partials/cockpit-skeleton'
import {useInitialDataLayer} from '../../tracking/hooks/use-initial-data-layer'
import useTrackViewPDP from './hooks/use-track-view-pdp'
import useInteractionStudioPixel from '../../tracking/interaction-studio'
import useStructuredData from '../../hooks/use-structured-data'
import Seo from '../../components/seo'
import {trackRRClickStars} from '../../tracking/analytics'
import useEffectEvent from '../../hooks/use-effect-event'
import useStoredConfiguration from './hooks/use-stored-configuration'
import ConfirmationModal from '../../components/confirmation-modal'
import {defineMessage} from 'react-intl'
import CategoriesBreadcrumb from '../../components/breadcrumb/categories-breadcrumb'
import HeaderContent from '../../components/header-content'
import InteractionStudioContent from '../../components/interaction-studio/content'
import InteractionStudioRecommendedProducts from '../../components/interaction-studio/recommended-products'
import {useCategory} from '@salesforce/commerce-sdk-react'
import useProductDetail from '../../hooks/useProductDetail'
import {Redirect, useParams} from 'react-router-dom'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useReviewsModal} from '../../components/reviews-modal'
import {hasBVProductRating} from '../../components/bazaarvoice/utils'
import ReviewOverview from '../../components/bazaarvoice/review-overview'
import {useDerivedProduct} from '../../hooks'
import useSlugUrlRedirect from './hooks/use-slug-url-redirect'
import {isServer} from '../../utils/utils'
import {css, Global} from '@emotion/react'
import theme from '../../theme'

// Dynamic modules - use ssr: false to prevent server side rendering
const fallback = <Skeleton height="25vh" width="100%" />
const ProductRecommendations = lazyLoadable(() => import('./partials/product-recommendations'), {
    fallback,
    ssr: false
})
const EmotionalGallery = lazyLoadable(() => import('../../components/emotional-gallery'), {
    fallback: <EmotionalGallerySkeleton />,
    ssr: false
})
const ProductUSP = lazyLoadable(() => import('./partials/product-usp'), {
    fallback: <ProductUSPSkeleton />,
    ssr: false
})
const ZoomModal = lazyLoadable(() => import('./partials/zoom-modal'), {
    fallback: <ImageGallerySkeleton />,
    ssr: false
})
const Specifications = lazyLoadable(
    () => import('../../pages/product-detail/partials/Specifications'),
    {
        fallback,
        ssr: false
    }
)
const AccessoryAdder = lazyLoadable(() => import('../../components/accessory-adder'), {
    fallback,
    ssr: false
})
const BackToTopLink = lazyLoadable(() => import('../../components/back-to-top-link'), {
    fallback,
    ssr: false
})
const RecommendedAccessories = lazyLoadable(
    () => import('../../components/recommended-accessories'),
    {
        fallback,
        ssr: false
    }
)
const RecentlyViewed = lazyLoadable(() => import('./partials/recently-viewed'), {
    fallback,
    ssr: false
})
// used for content bellow the fold to be lazy loaded
const LazyCMSContent = lazyLoadable(async () => CMSContent, {
    fallback,
    ssr: false
})
const Fanreel = lazyLoadable(() => import('../../components/fanreel'), {
    fallback,
    ssr: false
})

const RESTORE_CONFIGURATION_CONFIRMATION_DIALOG_CONFIG = {
    dialogTitle: defineMessage({
        defaultMessage: 'Stored configuration found',
        id: 'confirmation_modal.restore_configuration.title.confirm_restore'
    }),
    confirmationMessage: defineMessage({
        defaultMessage: 'Would you like to continue with your selected configuration?',
        id: 'confirmation_modal.restore_configuration.message.confirm_restore'
    })
}

const ProductDetail = () => {
    const {productId: initialProductId} = useParams()
    const {
        master: masterProduct,
        variant: variantProduct,
        isInitialLoading,
        isMasterLoading,
        isVariantLoading,
        isPriceLoading: isPromotionsLoading
    } = useProductDetail(initialProductId)
    const product = useUnifiedProduct(masterProduct, variantProduct)
    const productAsList = useMemo(() => [product], [product])
    const isProductASet = isProductSet(product)
    const {headerWrapper} = useHeader()
    const isSubnavigationVisible = headerWrapper
    const einstein = useEinstein()
    const isLoading = isInitialLoading || isMasterLoading
    const {pdpContentEnabled, bvEnabled} = useOcapiSite()
    const {onOpen: onOpenWriteModal} = useWriteProductReviewModal()
    const bvPid = useMemo(() => getBVReviewProductIds(product), [product])
    const hasReviews = useProductReviewOverview(bvPid)?.data?.review
    const derivedProduct = useDerivedProduct(product)
    const {formatMessage} = useIntl()
    const breakpoints = theme.breakpoints

    // Note: Since category needs id from product detail, it can't be server side rendered atm
    // until we can do dependent query on server
    const {
        data: category,
        isError: isCategoryError,
        error: categoryError
    } = useCategory({
        parameters: {
            id: product?.primaryCategoryId,
            levels: 1
        }
    })

    const {
        onOpen: onOpenReviewModal,
        isOpen: isOpenReviewModal,
        onClose: onCloseReviewModal
    } = useReviewsModal()

    const {productPageJSONLDScript, breadcrumbListJSONLDScript} = useStructuredData(
        category,
        product
    )

    /**************** Error Handling ****************/
    if (isCategoryError) {
        const errorStatus = categoryError?.response?.status
        switch (errorStatus) {
            case 404:
                throw new HTTPNotFound('Category Not Found.')
            default:
                throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
        }
    }

    /**************** Response Handling ****************/
    const {res} = useServerContext()
    if (res) {
        const cacheConfig = getConfig().app.cache
        res.set(
            'Cache-Control',
            cacheConfig?.pdp ||
                `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    useTrackViewPDP(product)
    useInteractionStudioPixel()
    // Reviews section is not available for product sets, spare parts and engraving
    const isReviewSectionEnabled = useMemo(
        () => bvEnabled && product && hasBVProductRating(product),
        [bvEnabled, product]
    )
    useInitialDataLayer({
        pageType: 'Product Detail'
    })
    const parentCategoryTree = useMemo(() => category?.parentCategoryTree || [], [category])
    /**************** Einstein ****************/
    const onSendViewProduct = useEffectEvent((product) => einstein.sendViewProduct(product))
    useEffect(() => {
        if (product) {
            if (product.type.set) {
                onSendViewProduct(product)
                const childrenProducts = product.setProducts
                childrenProducts.map((child) => {
                    onSendViewProduct(child)
                })
            } else {
                onSendViewProduct(product)
            }
        }
    }, [onSendViewProduct, product])
    /**************** Zoom modal ****************/
    const [zoomModal, setZoomModal] = useState()
    const [finalFocusRef, setFinalFocusRef] = useState()
    const zoomModalProps = useGenericModal()
    const showZoomModal = (state) => {
        setFinalFocusRef(state.finalFocusRef)
        setZoomModal(state)
        zoomModalProps.onOpen()
    }
    /* PDP Sections */
    const sections = {
        overview: useRef(),
        accessories: useRef(),
        specifications: useRef(),
        howToUse: useRef(),
        spareParts: useRef(),
        reviews: useRef()
    }

    const productQuickviewModalProps = useProductQuickviewModal()
    productQuickviewModalProps.onRecommendedProductClick = productQuickviewModalProps.onOpen

    // Hotspot
    const onHotspotClick = ({pid}) => {
        productQuickviewModalProps.onOpen(pid)
    }
    // When open notify me modal close quickview modal
    const onAddToCartClick = () => {
        productQuickviewModalProps.onClose()
    }
    // precompute if there are accessories
    const hasRecommendedAccessories = useMemo(
        () =>
            product?.recommendations?.filter(
                ({recommendationType}) => recommendationType.value === RECOMMENDED_ACCESSORIES_TYPE
            )?.length >= MINIMUN_NUM_OF_RECOMMENDED_PRODUCTS,
        [product?.recommendations]
    )

    const onRatingsClick = useCallback(() => {
        trackRRClickStars('page top')
        if (hasReviews) {
            onOpenReviewModal()
        } else {
            onOpenWriteModal(bvPid)
        }
    }, [onOpenWriteModal, bvPid, onOpenReviewModal, hasReviews])

    const {
        isAvailable: isStoredConfigurationAvailable,
        apply: applyStoredConfiguration,
        clear: clearStoredConfiguration
    } = useStoredConfiguration(product)
    const restoreConfigurationModalProps = useDisclosure({
        isOpen: isStoredConfigurationAvailable,
        onClose: clearStoredConfiguration
    })

    // redirect to slug url if it's different from the current url
    const redirectTo = useSlugUrlRedirect(isServer, initialProductId, masterProduct)
    if (redirectTo) {
        return <Redirect to={redirectTo} />
    }

    return (
        <Container
            layerStyle="page"
            variant="no-padding"
            className="sf-product-detail-page"
            data-testid="product-details-page"
        >
            <Global
                styles={css`
                    :root {
                        --pdp-subnavigation-height: 3.458rem;
                    }

                    @media screen and (min-width: ${breakpoints.lg}) {
                        :root {
                            --pdp-subnavigation-height: 5rem;
                        }
                    }
                `}
            />
            <Seo
                title={masterProduct?.pageTitle}
                description={masterProduct?.pageDescription}
                keywords={masterProduct?.pageKeywords}
            />
            <Helmet>
                <meta property="evg:productId" content={product?.id} />
                <link rel="canonical" href={product?.c_canonicalUrl} />
                {productPageJSONLDScript}
                {breadcrumbListJSONLDScript}
            </Helmet>
            {isSubnavigationVisible &&
                createPortal(
                    <PdpSubnavigation
                        isGetPropsLoading={isInitialLoading}
                        isPromotionsLoading={isPromotionsLoading}
                        isVariantLoading={isVariantLoading}
                        product={product}
                        quantity={derivedProduct.quantity}
                        sections={sections}
                    />,
                    headerWrapper
                )}
            <Flex direction="column" alignItems="stretch">
                {/* CategoriesBreadcrumb */}
                <CategoriesBreadcrumb categories={parentCategoryTree} />
                <HeaderContent />
                {/* Emotional intro */}
                {product?.c_emotionalIntro && pdpContentEnabled && (
                    <InteractionStudioContent id="is-pdp-emotinal-intro">
                        <CMSContent id={product.c_emotionalIntro} showError={false} />
                    </InteractionStudioContent>
                )}
                {/* Top Content */}
                {product?.c_pdpTopContent && pdpContentEnabled && (
                    <InteractionStudioContent id="is-pdp-top-content">
                        <CMSContent id={product.c_pdpTopContent} showError={false} />
                    </InteractionStudioContent>
                )}
                {/* Cockpit */}
                <Box
                    ref={sections.overview}
                    paddingTop={{
                        base: 4,
                        lg: 8
                    }}
                    paddingBottom={12}
                    px={{base: 0, lg: 8}}
                >
                    {isLoading || !product ? (
                        <CockpitSkeleton />
                    ) : (
                        <>
                            {isProductASet ? (
                                <ProductSetCockpit
                                    derivedProduct={derivedProduct}
                                    product={product}
                                    category={parentCategoryTree}
                                    isGetPropsLoading={isInitialLoading}
                                    isMasterLoading={isMasterLoading}
                                    isPromotionsLoading={isPromotionsLoading}
                                    isVariantLoading={isVariantLoading}
                                    isProductASet={isProductASet}
                                    onAddToCartClick={onAddToCartClick}
                                    showZoomModal={showZoomModal}
                                    onHotspotClick={onHotspotClick}
                                    onRatingsClick={onRatingsClick}
                                />
                            ) : (
                                <Cockpit
                                    product={product}
                                    derivedProduct={derivedProduct}
                                    category={parentCategoryTree}
                                    isMasterLoading={isMasterLoading}
                                    isPromotionsLoading={isPromotionsLoading}
                                    isVariantLoading={isVariantLoading}
                                    showZoomModal={showZoomModal}
                                    onAddToCartClick={onAddToCartClick}
                                    onHotspotClick={onHotspotClick}
                                    onRecommendedProductClick={productQuickviewModalProps.onOpen}
                                    onRatingsClick={onRatingsClick}
                                />
                            )}
                        </>
                    )}
                </Box>

                {/* Mid Content */}
                {product?.c_pdpMidContent && pdpContentEnabled && (
                    <InteractionStudioContent id="is-pdp-mid-content">
                        <LazyCMSContent id={product.c_pdpMidContent} showError={false} />
                    </InteractionStudioContent>
                )}

                {/* Accessory adder */}
                <AccessoryAdder
                    product={product}
                    onRecommendedProductClick={productQuickviewModalProps.onOpen}
                />

                {/* Emotional gallery */}
                <EmotionalGallery
                    imageGroups={product?.imageGroups}
                    selectedVariationAttributes={product?.variationValues}
                    extraCarouselImage={product?.c_extraCarouselImage}
                />

                {/* USP */}
                <Box paddingTop={10} paddingBottom={12}>
                    <ProductUSP product={product} />
                </Box>

                {/* Bottom Content */}
                {product?.c_pdpBottomContent && pdpContentEnabled && (
                    <InteractionStudioContent id="is-pdp-bottom-content">
                        <LazyCMSContent id={product.c_pdpBottomContent} showError={false} />
                    </InteractionStudioContent>
                )}

                {/* Reco (Accessories) */}
                {hasRecommendedAccessories && (
                    <InteractionStudioRecommendedProducts id="is-pdp-accessories">
                        <RecommendedAccessories
                            ref={sections.accessories}
                            products={productAsList}
                            onRecommendedProductClick={productQuickviewModalProps.onOpen}
                        />
                    </InteractionStudioRecommendedProducts>
                )}

                {/* Specification */}
                <Box ref={sections.specifications} background="stokkeGray.bright">
                    <Specifications product={product}></Specifications>
                </Box>

                {/* FAQs */}
                <AfterSales
                    product={product}
                    sections={sections}
                    onRecommendedProductClick={productQuickviewModalProps.onOpen}
                />

                {/* Social */}
                {product?.c_curalate ? <Fanreel product={product} /> : null}

                {/* Reviews */}
                {isReviewSectionEnabled && (
                    <ReviewOverview
                        ref={sections.reviews}
                        product={product}
                        onCloseReviewModal={onCloseReviewModal}
                        isOpenReviewModal={isOpenReviewModal}
                        onOpenReviewModal={onRatingsClick}
                    />
                )}

                {/* Bottom Content2 */}
                {product?.c_pdpBottomContent2 && pdpContentEnabled && (
                    <InteractionStudioContent id="is-pdp-bottom-content2">
                        <LazyCMSContent id={product.c_pdpBottomContent2} showError={false} />
                    </InteractionStudioContent>
                )}

                {/* Reco */}
                <InteractionStudioRecommendedProducts id="is-pdp-recommendations">
                    <ProductRecommendations
                        product={product}
                        onRecommendedProductClick={productQuickviewModalProps.onOpen}
                    />
                </InteractionStudioRecommendedProducts>

                {/* Recently viewed */}
                <InteractionStudioRecommendedProducts id="is-pdp-recently-viewed">
                    <RecentlyViewed onRecommendedProductClick={productQuickviewModalProps.onOpen} />
                </InteractionStudioRecommendedProducts>

                {/* Back to top */}
                <Box background="stokkeGray.bright" paddingTop={4} paddingBottom={10}>
                    <Flex justifyContent="center">
                        <BackToTopLink />
                    </Flex>
                </Box>
            </Flex>
            <GenericModal
                {...zoomModalProps}
                size="full"
                colorScheme="white"
                areaLabel={formatMessage({
                    id: 'image_gallery.label',
                    defaultMessage: 'Zoomed-in view of product images'
                })}
                onClose={() => {
                    zoomModalProps?.onClose()
                    // works only in case there is a slight delay and it should be more than 100ms
                    setTimeout(() => {
                        finalFocusRef?.focus()
                    }, 500)
                }}
            >
                {/* zoom modal is loaded async client side for performance, but the generic modal is already available,
                    so user can open the modal and see placeholder while loading */}
                <ZoomModal {...zoomModal} product={product} />
            </GenericModal>
            <ProductQuickviewModal {...productQuickviewModalProps} />
            <ConfirmationModal
                {...restoreConfigurationModalProps}
                {...RESTORE_CONFIGURATION_CONFIRMATION_DIALOG_CONFIG}
                onPrimaryAction={applyStoredConfiguration}
                onAlternateAction={clearStoredConfiguration}
            />
        </Container>
    )
}
ProductDetail.getTemplateName = () => 'product-detail'
ProductDetail.shouldGetProps = () => false

export default ProductDetail
